import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/lib/env';
import { APP_PLATFORM } from '@/lib/platform';
import { APP_VERSION } from '@/lib/version';
import { useUpdateStore } from '@/stores/updateStore';

export interface Envelope<T> {
  code: number;
  message: string;
  data: T;
  meta?: unknown;
}

type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

export const api = axios.create({
  baseURL: env.VITE_VENDOR_BASE_URL,
  withCredentials: true,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    // Only the packaged native builds self-report platform+version — plain
    // web/PWA traffic never sends these (hoopla-vendor's update middleware
    // no-ops when they're absent).
    ...(APP_PLATFORM !== 'web'
      ? { 'X-App-Platform': APP_PLATFORM, 'X-App-Version': APP_VERSION }
      : {}),
  },
});

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

api.interceptors.response.use(
  (res) => {
    useUpdateStore.getState().applyUpdateHeaders(res.headers as Record<string, string>);
    return res;
  },
  async (error: AxiosError) => {
    useUpdateStore.getState().applyUpdateHeaders(error.response?.headers as Record<string, string> | undefined);

    const original = error.config as RetryableConfig | undefined;
    if (!original) return Promise.reject(error);

    if (error.response?.status === 401 && !original._retried) {
      original._retried = true;
      try {
        return await api.request(original);
      } catch (retryError) {
        const retryAxiosError = retryError as AxiosError;
        if (retryAxiosError.response?.status === 401) {
          onUnauthorized?.();
        }
        return Promise.reject(retryError);
      }
    }
    return Promise.reject(error);
  },
);

export function unwrap<T>(res: AxiosResponse<Envelope<T>>): T {
  return res.data.data;
}

export interface ApiError {
  status: number;
  message: string;
}

export function toApiError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Envelope<unknown> | undefined;
    return {
      status: err.response?.status ?? 0,
      message: data?.message || err.message || 'Network error',
    };
  }
  return { status: 0, message: (err as Error)?.message ?? 'Unknown error' };
}
